import React, { useRef, useEffect, useState } from 'react'
import { Group, Text, Rect, Image, Transformer } from 'react-konva'
import placeholderImage from '../../../assets/placeholder_photo.jpg'

/**
 * TransformableImage - A draggable, resizable image placeholder for the canvas
 */
export default function TransformableImage({
  field,
  isSelected,
  onSelect,
  onChange,
  onContextMenu,
  hideBoundingBoxes
}) {
  const groupRef = useRef()
  const trRef = useRef()
  const [image, setImage] = useState(null)

  // Load placeholder image
  useEffect(() => {
    const img = new window.Image()
    img.src = placeholderImage
    img.onload = () => setImage(img)
  }, [])

  useEffect(() => {
    if (isSelected && trRef.current && groupRef.current) {
      trRef.current.nodes([groupRef.current])
      trRef.current.getLayer().batchDraw()
    }
  }, [isSelected])

  // Default to 4:5 aspect ratio (80x100)
  const width = field.width || 80
  const height = field.height || 100

  return (
    <>
      <Group
        ref={groupRef}
        x={field.left || 0}
        y={field.top || 0}
        width={width}
        height={height}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onContextMenu={onContextMenu}
        onDragEnd={(e) => {
          onChange({
            ...field,
            left: Math.round(e.target.x()),
            top: Math.round(e.target.y()),
          })
        }}
        onTransformEnd={(e) => {
          const node = groupRef.current
          const scaleX = node.scaleX()
          const scaleY = node.scaleY()

          node.scaleX(1)
          node.scaleY(1)

          onChange({
            ...field,
            left: Math.round(node.x()),
            top: Math.round(node.y()),
            width: Math.round(Math.max(30, width * scaleX)),
            height: Math.round(Math.max(30, height * scaleY)),
          })
        }}
      >
        {/* Placeholder image or gray rectangle fallback - always visible */}
        {image ? (
          <Image
            x={0}
            y={0}
            width={width}
            height={height}
            image={image}
            stroke="#ffffff"
            strokeWidth={4}
          />
        ) : (
          <Rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="#e0e0e0"
            stroke="#ffffff"
            strokeWidth={4}
          />
        )}
        {/* Selection border overlay (only when editing) */}
        {!hideBoundingBoxes && (
          <Rect
            x={-1}
            y={-1}
            width={width + 2}
            height={height + 2}
            fill="transparent"
            stroke={isSelected ? '#4a90d9' : '#888888'}
            strokeWidth={1}
            dash={isSelected ? [] : [4, 4]}
          />
        )}
        {/* Label for image placeholder */}
        {!hideBoundingBoxes && (
          <Text
            x={5}
            y={5}
            text={field.label || 'Image'}
            fontSize={10}
            fill="#ffffff"
            shadowColor="#000000"
            shadowBlur={2}
            shadowOpacity={0.8}
          />
        )}
      </Group>
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 30 || newBox.height < 30) {
              return oldBox
            }
            return newBox
          }}
          anchorSize={8}
          anchorCornerRadius={1}
          anchorStroke="#4a90d9"
          anchorFill="#ffffff"
          borderStroke="#4a90d9"
          borderStrokeWidth={1}
          enabledAnchors={['top-left', 'top-right', 'bottom-right', 'bottom-left']}
          rotateEnabled={false}
          keepRatio={true}
        />
      )}
    </>
  )
}
